import { Link, Navigate, useParams } from "react-router-dom";
import { LogOut, BookOpen, MessageSquare, ChevronDown, User, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import { COURSES } from "@/data/courses";

export default function CoursePage() {
  const { courseId } = useParams();
  const { user, logout } = useAuth();
  const course = COURSES.find((c) => c.id === courseId);

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#fffbf2] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#e65100] border-t-transparent rounded-full animate-spin" aria-label="Cargando" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!course) return <Navigate to="/campus" replace />;

  return (
    <div data-testid="course-page" className="min-h-screen bg-[#fffbf2] text-[#0f2027]">
      <header className="bg-[#e65100] text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/campus" className="flex items-center gap-3">
            <BrandMark className="w-9 h-9" />
            <div className="leading-none">
              <span className="block font-display text-sm tracking-wider uppercase">PROTECCIÓN CIVIL VIGO</span>
              <span className="block text-[10px] tracking-widest text-white/80 font-mono-tactical uppercase">
                Campus de Teleformación
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs font-mono-tactical uppercase tracking-widest text-white/80">
              {user.name}
            </span>
            <button
              onClick={logout}
              data-testid="course-logout-btn"
              className="flex items-center gap-2 bg-[#0f2027] px-4 py-2 text-xs font-mono-tactical uppercase tracking-wider font-semibold rounded hover:bg-[#162c35] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 space-y-10">
        <div className="space-y-3">
          <Link
            to="/campus"
            data-testid="course-back-link"
            className="inline-flex items-center gap-2 text-xs font-mono-tactical uppercase tracking-widest text-[#e65100] hover:underline"
          >
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
            Volver a cursos
          </Link>
          <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">{course.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs font-mono-tactical uppercase">
            <span className="bg-[#e65100] text-white px-2.5 py-1 rounded font-bold">Activo</span>
            <span className="text-[#0f2027]/70 font-semibold flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" aria-hidden="true" />
              {course.lessons}
            </span>
            <span className="text-[#0f2027]/70">Profesorado: {course.instructors.join(" · ")}</span>
          </div>
          <p className="text-sm md:text-base text-[#0f2027]/80 max-w-2xl leading-relaxed">{course.desc}</p>
        </div>

        <div className="space-y-4">
          {course.units.map((unit) => (
            <section
              key={unit.name}
              data-testid={`course-unit-${unit.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] overflow-hidden"
            >
              <div className="px-6 py-4 bg-[#f7eedc] border-b border-[#0f2027]/10 flex items-center justify-between">
                <h2 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
                  <ChevronDown className="w-4 h-4 text-[#e65100]" aria-hidden="true" />
                  {unit.name}
                </h2>
              </div>
              <ul className="divide-y divide-[#0f2027]/10">
                {unit.topics.map((topic) => (
                  <li
                    key={topic}
                    className="px-6 py-4 flex items-center gap-3 text-sm text-[#0f2027]/85 hover:bg-[#fffbf2] transition-colors"
                  >
                    <MessageSquare className="w-4 h-4 text-[#e65100] shrink-0" aria-hidden="true" />
                    <span className="font-medium">{topic}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="p-8 bg-[#0f2027] text-[#fffbf2] rounded-lg flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="font-display text-2xl uppercase">¿Dudas sobre este curso?</h3>
            <p className="text-sm text-[#fffbf2]/70 flex items-center gap-2">
              <User className="w-4 h-4 text-[#e65100]" aria-hidden="true" />
              Escríbenos y te respondemos lo antes posible.
            </p>
          </div>
          <a
            href="mailto:info@busquedaysalvamento.es"
            className="bg-[#e65100] text-white px-6 py-3 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#c2410c] transition-colors"
          >
            info@busquedaysalvamento.es
          </a>
        </div>
      </main>
    </div>
  );
}
